import React from "react";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import type {
  CsatFormComponentConfig,
  FlowComponentRenderProps,
} from "../../types";
import sentimentNeutral from "../../../assets/images/sentiment-neutral-v2.svg";
import sentimentSatisfied from "../../../assets/images/sentiment-satisfied-v2.svg";
import sentimentVerySatisfied from "../../../assets/images/sentiment-very-satisfied-v2.svg";
import sentimentVeryDissatisfied from "../../../assets/images/sentiment-very-dissatisfied-v2.svg";
import HtmlText from "../HtmlText";
import InlineFeedback from "./InlineFeedback";
import RatingSubmitArea from "./RatingSubmitArea";
import {
  buildRatingValue,
  defaultButtonStyles,
  getRatingState,
  selectedButtonStyles,
} from "./ratingUtils";
import { useCompanyBrandColorScheme } from '../../CompanyBrandProvider';

const csatFaceIconByValue: Record<number, string> = {
  1: sentimentVeryDissatisfied,
  2: sentimentNeutral,
  3: sentimentSatisfied,
  4: sentimentVerySatisfied,
};

const CsatFormBlock: React.FC<
  FlowComponentRenderProps<CsatFormComponentConfig>
> = ({ config, goNext, setValue, submitValueAndGoNext, value }) => {
  const ratingState = getRatingState(value);
  const selectedValue = ratingState.score;
  const submitted = ratingState.submitted;
  const selectionLocked = submitted && config.allowResubmit === false;
  const layout = config.layout ?? "row";
  const buttonStyle = config.style ?? "faces_labels";
  const colorCoding = config.colorCoding === true;
  const glassExperience = useCompanyBrandColorScheme() === 'dark';
  const options = config.options;
  const optionColor = (value: number) =>
    value <= 2
      ? (config.negativeColor ?? "#FECACA")
      : value === 3
        ? (config.neutralColor ?? "#FEF3C7")
        : (config.positiveColor ?? "#DCFCE7");

  return (
    <Card
      sx={{
        bgcolor: "transparent",
        border: 0,
        borderRadius: 0,
        boxShadow: "none",
        color: "var(--cx-brand-text)",
        p: 0,
      }}
    >
      <Stack spacing={1.5}>
        <HtmlText
          component="div"
          html={config.question ?? "Rate your experience"}
          variant="subtitle2"
          sx={{
            fontSize: glassExperience ? 26 : 16,
            fontWeight: glassExperience ? 500 : 600,
            lineHeight: glassExperience ? '32px' : '24px',
            textAlign: glassExperience ? 'left' : 'center',
          }}
        />
        {glassExperience ? (
          <Typography sx={{ color: 'var(--cx-brand-text-muted)', fontSize: 14 }}>
            Your feedback takes about 5 seconds.
          </Typography>
        ) : null}
        <Box
          sx={{
            display: "grid",
            gap: 1,
            gridTemplateColumns:
              layout === "vertical"
                ? "1fr"
                : layout === "grid"
                  ? "repeat(2, minmax(0, 1fr))"
                : `repeat(${options.length}, minmax(0, 1fr))`,
          }}
        >
          {options.map((option) => {
            const selected = selectedValue === option.value;
            const faceIcon =
              csatFaceIconByValue[option.value] ?? sentimentSatisfied;
            const faceIconSize = buttonStyle === "compact" ? 24 : 34;

            return (
              <Button
                aria-pressed={selected}
                disabled={selectionLocked}
                key={option.value}
                onClick={() =>
                  setValue(
                    config.id,
                    buildRatingValue(config, ratingState, option.value, false),
                  )
                }
                sx={{
                  ...(selected ? selectedButtonStyles : defaultButtonStyles),
                  bgcolor: selected
                    ? "var(--cx-brand-button-selected-bg)"
                    : colorCoding
                      ? optionColor(option.value)
                      : "var(--cx-brand-button-bg)",
                  border: "1px solid",
                  borderRadius: 0.75,
                  flexDirection:
                    layout === "vertical" || buttonStyle === "compact"
                      ? "row"
                      : "column",
                  fontWeight: 600,
                  gap: 0.5,
                  height: buttonStyle === "compact" ? 48 : 86,
                  minHeight: buttonStyle === "compact" ? 48 : 86,
                  px: 0.5,
                  transition: selected ? "none" : undefined,
                  '[data-cx-color-scheme="dark"] &': {
                    backdropFilter: 'blur(12px)',
                    background: selected
                      ? 'var(--cx-brand-button-selected-bg)'
                      : 'color-mix(in srgb, var(--cx-brand-button-bg) 76%, transparent)',
                    borderColor: selected
                      ? 'var(--cx-brand-button-selected-border)'
                      : 'var(--cx-brand-border)',
                    borderRadius: 3,
                    height: buttonStyle === 'compact' ? 52 : 92,
                  },
                  "&:hover": {
                    bgcolor: selected
                      ? "var(--cx-brand-button-selected-bg)"
                      : colorCoding
                        ? optionColor(option.value)
                        : "var(--cx-brand-button-hover-bg)",
                    borderColor: selected
                      ? "var(--cx-brand-button-selected-border)"
                      : "var(--cx-brand-button-border)",
                  },
                  "&:disabled": {
                    color: selected
                      ? "var(--cx-brand-button-selected-text)"
                      : "var(--cx-brand-text-muted)",
                    opacity: 0.75,
                  },
                }}
                type="button"
                variant="outlined"
              >
                {buttonStyle !== "labels" ? (
                  <Box
                    aria-hidden="true"
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      height: faceIconSize,
                      justifyContent: "center",
                      width: faceIconSize,
                    }}
                  >
                    <Box
                      aria-hidden="true"
                      data-testid={`csat-face-${option.value}`}
                      sx={{
                        bgcolor: "currentColor",
                        height: `${faceIconSize * (5 / 6)}px`,
                        maskImage: `url("${faceIcon}")`,
                        maskPosition: "center",
                        maskRepeat: "no-repeat",
                        maskSize: "contain",
                        WebkitMaskImage: `url("${faceIcon}")`,
                        WebkitMaskPosition: "center",
                        WebkitMaskRepeat: "no-repeat",
                        WebkitMaskSize: "contain",
                        width: `${faceIconSize * (5 / 6)}px`,
                      }}
                    />
                  </Box>
                ) : null}
                {buttonStyle !== "faces" ? (
                  <Typography
                    component="span"
                    sx={{
                      color: "inherit",
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: "22px",
                    }}
                  >
                    {option.label}
                  </Typography>
                ) : null}
              </Button>
            );
          })}
        </Box>
        <InlineFeedback
          config={config}
          locked={selectionLocked}
          ratingState={ratingState}
          setValue={setValue}
        />
        <RatingSubmitArea
          config={config}
          continueToNext={goNext}
          onEdit={() =>
            setValue(config.id, {
              ...ratingState,
              submitted: false,
            })
          }
          ratingState={ratingState}
          selectedValue={selectedValue}
          submitted={submitted}
          submitValueAndGoNext={submitValueAndGoNext}
        />
      </Stack>
    </Card>
  );
};

export default CsatFormBlock;
